import { API_ENDPOINTS } from '../constants/api';
import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { TIMEOUT_CONFIG } from '../constants/common';

class TransactionService {
  /**
   * Get user transactions with pagination
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of transactions to fetch (default: 5)
   * @param {number} params.skip - Number of transactions to skip (default: 0)
   * @returns {Promise<Object>} Transactions response with data, pagination info
   */
  async getTransactions(params = {}) {
    try {
      // Set default values
      const queryParams = {
        limit: params.limit || 5,
        skip: params.skip || 0
      };

      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');

      const url = `${API_ENDPOINTS.TRANSACTIONS}?${queryString}`;
      const response = await apiClient.get(url);

      // Preserve the full response structure with pagination info
      // The API returns: { data: [...], totalPages, recordsTotal, perPage, currentPage }
      return {
        success: true,
        status: response.status || 200,
        message: 'Transactions fetched successfully',
        data: response.data, // Preserve full structure with data array and pagination fields
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch transactions');
    }
  }

  /**
   * Get balance summary for content creator
   * @returns {Promise<Object>} Balance summary response with data
   */
  async getBalanceSummary() {
    try {
      const url = `${API_ENDPOINTS.BALANCE_SUMMARY}`;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Balance summary fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch balance summary');
    }
  }

  /**
   * Get today earnings summary for content creator
   * @param {string} startDate - Start date
   * @param {string} endDate - End date
   * @returns {Promise<Object>} Today earnings summary response with data
   */
  async getTodayEarningsSummary(startDate, endDate) {
    try {
      const queryParams = {
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      };
      
      const queryString = Object.keys(queryParams).length > 0 
        ? `?${new URLSearchParams(queryParams).toString()}`
        : '';
      
      const url = `${API_ENDPOINTS.TODAY_EARNINGS_SUMMARY}${queryString}`;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Today earnings summary fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch today earnings summary');
    }
  }

  /**
   * Get daily earnings breakdown for content creator
   * @returns {Promise<Object>} Daily earnings breakdown response with data
   */
  async getDailyEarnings() {
    try {
      const url = API_ENDPOINTS.DAILY_EARNINGS;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Daily earnings fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch daily earnings');
    }
  }

  /**
   * Get monthly earnings breakdown for content creator
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of months to fetch (default: 10)
   * @param {number} params.skip - Number of months to skip (default: 0)
   * @param {number} params.year - Year to fetch earnings for
   * @param {number} params.month - Month to fetch earnings for (1-12)
   * @returns {Promise<Object>} Monthly earnings breakdown response with data
   */
  async getMonthlyEarnings(params = {}) {
    try {
      // Set default values
      const queryParams = {
        limit: params.limit || 10,
        skip: params.skip || 0,
      };

      // Add optional year and month if provided
      if (params.year !== undefined && params.year !== null) {
        queryParams.year = params.year;
      }
      if (params.month !== undefined && params.month !== null) {
        queryParams.month = params.month;
      }

      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');

      const url = `${API_ENDPOINTS.MONTHLY_EARNINGS}?${queryString}`;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Monthly earnings fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch monthly earnings');
    }
  }

  /**
   * Get total earnings breakdown for content creator
   * @returns {Promise<Object>} Total earnings breakdown response with data
   */
  async getTotalEarningsBreakdown() {
    try {
      const url = API_ENDPOINTS.TOTAL_EARNINGS_BREAKDOWN;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Total earnings breakdown fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch total earnings breakdown');
    }
  }

  /**
   * Get total earnings yearly for content creator
   * @param {number|string} year - Year to fetch earnings for
   * @returns {Promise<Object>} Total earnings yearly response with data
   */
  async getTotalEarningsYearly(year) {
    try {
      const queryParams = {
        year: year
      };

      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');

      const url = `${API_ENDPOINTS.TOTAL_EARNINGS_YEARLY}?${queryString}`;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Total earnings yearly fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch total earnings yearly');
    }
  }

  /**
   * Request payout for content creator
   * @returns {Promise<Object>} Payout request response with status and message
   */
  async requestPayout() {
    try {
      const url = API_ENDPOINTS.REQUEST_PAYOUT;
      const response = await apiClient.post(url, {}, {
        timeout: TIMEOUT_CONFIG.PAYOUT_REQUEST,
      });

      return {
        success: true,
        status: response.status || 200,
        message: response.data?.message || 'Payout request sent!',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      // Preserve requirements data if present in error response
      const errorResponse = {
        success: false,
        status: error.response?.status,
        message: error.response?.data?.message || 'Failed to request payout',
        requirements: error.response?.data?.requirements || null,
        data: error.response?.data || null
      };
      throw errorResponse;
    }
  }

  /**
   * Create W-9 tax form for the current user
   * @param {Object} formData - W-9 form payload
   * @returns {Promise<Object>} W-9 creation response with status and message
   */
  async createW9Form(formData) {
    try {
      // const response = await apiClient.post(url, formData);
      const response = await apiClient.post(API_ENDPOINTS.W9_CREATE_FORM, formData, {
        timeout: TIMEOUT_CONFIG.W9_FORM,
      });

      return {
        success: true,
        status: response.status || 200,
        message: response.data?.message || 'W-9 form submitted successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      let errors = error?.response?.data?.taxBanditsResponse?.Errors || error?.response?.data?.errors || [];
      if (errors.length > 0) {
        if (error?.response) {
          error.response.data = {
            ...error.response.data,
            errors: errors
          };
        }
      }
      throw ResponseHandler.handleError(error, 'Failed to submit W-9 form');
    }
  }

  /**
   * Create W-8 tax form for the current user
   * @param {Object} formData - W-8 form payload
   * @returns {Promise<Object>} W-8 creation response with status and message
   */
  async createW8Form(formData) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.W8_CREATE_FORM, formData, {
        timeout: TIMEOUT_CONFIG.W8_FORM,
      });

      return {
        success: true,
        status: response.status || 200,
        message: response.data?.message || 'W-8 form submitted successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      let errors = error?.response?.data?.taxBanditsResponse?.Errors || error?.response?.data?.errors || [];
      if (errors.length > 0) {
        if (error?.response) {
          error.response.data = {
            ...error.response.data,
            errors: errors
          };
        }
      }
      throw ResponseHandler.handleError(error, 'Failed to submit W-8 form');
    }
  }

  /**
   * Get W-9 tax form for the current user
   * @returns {Promise<Object>} W-9 form response with data
   */
  async getW9Form() {
    try {
      const url = API_ENDPOINTS.W9_GET_FORM;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'W-9 form fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch W-9 form');
    }
  }
  
  /**
   * Get W-8 tax form for the current user
   * @returns {Promise<Object>} W-8 form response with data
   */
  async getW8Form() {
    try {
      const url = API_ENDPOINTS.W8_GET_FORM;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'W-8 form fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch W-8 form');
    }
  }

  /**
   * Lookup user information for masspay
   * @returns {Promise<Object>} User lookup response with data
   */
  async lookupUser() {
    try {
      // Use full URL since this endpoint uses a different base URL
      const url = API_ENDPOINTS.MASSPAY_LOOKUP_USER;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'User lookup fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to lookup user');
    }
  }

  /**
   * List services for masspay
   * @returns {Promise<Object>} List services response with data
   */
  async listServices() {
    try {
      const url = API_ENDPOINTS.MASSPAY_LIST_SERVICES;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Services list fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch services list');
    }
  }

  /**
   * Get masspay attributes
   * @param {Object} params - Query parameters object
   * @param {string} params.currency - Currency code (e.g., 'USD')
   * @param {string} params.destination_token - Destination token
   * @param {string} params.user_token - User token
   * @returns {Promise<Object>} Masspay attributes response with data
   */
  async getMasspayAttributes(params = {}) {
    try {
      // Build query string from params object
      const queryParams = {
        currency: params.currency || 'USD',
        destination_token: params.destination_token,
        user_token: params.user_token
      };

      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');

      const url = `${API_ENDPOINTS.MASSPAY_ATTRIBUTES}?${queryString}`;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Masspay attributes fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch masspay attributes');
    }
  }

  /**
   * Get payment method information
   * @returns {Promise<Object>} Payment method response with data
   */
  async getPaymentMethod() {
    try {
      const url = API_ENDPOINTS.MASSPAY_PAYMENT_METHOD;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Payment method fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch payment method');
    }
  }

  /**
   * Send change payout method confirmation link to the user's email
   * @returns {Promise<Object>} Send link response with status and message
   */
  async sendPaymentMethodChangeLink() {
    try {
      const url = API_ENDPOINTS.PAYMENT_METHOD_SEND_CHANGE_LINK;
      const response = await apiClient.post(url, {});

      return {
        success: true,
        status: response.status || 200,
        message: response.data?.message || 'Confirmation link sent successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to send confirmation link');
    }
  }

  /**
   * Validate payout method change code (from email confirmation link)
   * @param {string} code - Change code from URL query param
   * @returns {Promise<Object>} Validation response with status/message
   */
  async validatePaymentMethodChangeCode(code) {
    try {
      const queryString = new URLSearchParams({ code }).toString();
      const url = `${API_ENDPOINTS.PAYMENT_METHOD_VALIDATE_CHANGE_CODE}?${queryString}`;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: response.data?.message || 'Change code validated successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to validate change code');
    }
  }

  /**
   * create masspay account
   * @param {Object} attributesData - Attributes data object
   * @param {string} attributesData.user_token - User token
   * @param {string} attributesData.destination_token - Destination token
   * @param {string} attributesData.currency - Currency code (e.g., 'USD')
   * @param {string} attributesData.company - Company name
   * @param {string} attributesData.payer - Payer name
   * @param {Array} attributesData.attributes - Array of attribute objects with token and value
   * @returns {Promise<Object>} Set attributes response with status and message
   */
  async createMasspayAccount(attributesData) {
    try {
      const url = API_ENDPOINTS.MASSPAY_SET_ATTRIBUTES;
      const response = await apiClient.post(url, attributesData);

      return {
        success: true,
        status: response.status || 200,
        message: response.data?.message || 'Attributes saved successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to set masspay attributes');
    }
  }

  /**
   * Get transaction history for masspay
   * @param {Object} params - Query parameters object
   * @param {number} params.number_of_records - Number of records to fetch
   * @param {string} params.start_date - Start date (YYYY-MM-DD format)
   * @param {string} params.end_date - End date (YYYY-MM-DD format)
   * @param {number} params.page - Page number
   * @returns {Promise<Object>} Transaction history response with data
   */
  async getTransactionHistory(params = {}) {
    try {
      const queryParams = {};
      
      if (params.number_of_records !== undefined && params.number_of_records !== null) {
        queryParams.number_of_records = params.number_of_records;
      }
      if (params.start_date) {
        queryParams.start_date = params.start_date;
      }
      if (params.end_date) {
        queryParams.end_date = params.end_date;
      }
      if (params.page !== undefined && params.page !== null) {
        queryParams.page = params.page;
      }
      
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');

      const url = queryString ? `${API_ENDPOINTS.MASSPAY_TRANSACTION_HISTORY}?${queryString}` : API_ENDPOINTS.MASSPAY_TRANSACTION_HISTORY;
      const response = await apiClient.get(url);

      return {
        success: true,
        status: response.status || 200,
        message: 'Transaction history fetched successfully',
        data: response.data,
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch transaction history');
    }
  }

  /**
   * Export earnings report as PDF
   * @param {string} html - HTML content to convert to PDF
   * @returns {Promise<Object>} PDF export response with blob data
   */
  async exportEarningsPdf(html) {
    try {
      const url = API_ENDPOINTS.EARNING_EXPORT_PDF;
      // Send HTML as-is - axios will JSON.stringify it correctly
      // The \041 in curl output is just how curl displays ! character, actual JSON is correct
      const response = await apiClient.post(url, { html }, {
        responseType: 'blob', // Important: expect binary data (PDF)
        timeout: TIMEOUT_CONFIG.PDF,
      });

      return {
        success: true,
        status: response.status || 200,
        message: 'PDF exported successfully',
        data: response.data, // This will be a Blob
        meta: {
          timestamp: new Date().toISOString(),
          statusCode: response.status || 200,
        }
      };
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to export PDF');
    }
  }
}

export default new TransactionService();

